/*
 * Sample sales data (P4): leads, opportunities, proposals & quotations, demos
 * and sales targets.
 *
 * The story joins up with the rest of the prototype: leads converted from
 * prospects carry the prospect's `converted_lead` code, won opportunities point
 * at customers in mock/customers.js, and owners, products, packages,
 * competitors and reason codes all come from mock/masters.js by id.
 */
import { daysFromNow } from "@/helpers/crm";
import { leadQualification, pipelineStages, packages, staffName } from "@/mock/masters";

/*
 * Discount control (P4 Phase 2). A company setting under Settings → Approval
 * Matrix: a quote whose overall discount is above the threshold goes for
 * approval, and the size of the discount decides how far up it goes.
 * NEW MASTER: belongs to settings when the backend is built.
 */
export const discountPolicy = {
  threshold: 10, // % above which a quote needs approval
  vat: 16, // Kenyan VAT, %
  validity_days: 30,
  matrix: [
    { from: 10, to: 20, chain: ["Sales Manager"] },
    { from: 20, to: 30, chain: ["Sales Manager", "Head of Sales"] },
    { from: 30, to: 100, chain: ["Sales Manager", "Head of Sales", "Finance"] },
  ],
};

export const approvalChainFor = (discountPct) =>
  discountPct <= discountPolicy.threshold ? [] : (discountPolicy.matrix.find((m) => discountPct > m.from && discountPct <= m.to) ?? discountPolicy.matrix.at(-1)).chain;

// Lead scoring (Advanced): the bands are a company setting.
export const scoreBands = [
  { key: "hot", name: "Hot", min: 70, tone: "danger", icon: "iconoir-fire-flame" },
  { key: "warm", name: "Warm", min: 40, tone: "warning", icon: "iconoir-half-moon" },
  { key: "cold", name: "Cold", min: 0, tone: "info", icon: "iconoir-snow-flake" },
];
export const scoreBand = (score) => scoreBands.find((b) => score >= b.min);

/* ------------------------------------------------------------------ Leads */

const Q_TEXT = {
  need: "Automate payroll, statutory returns (PAYE, NSSF, SHIF, Housing Levy) and leave for all branches.",
  pain_point: "Payroll takes 4 days each month in Excel; KRA penalties last year for late P10s.",
  budget: "KES 600K–900K a year approved in the FY budget.",
  decision_maker: "Finance Director signs; HR Manager recommends.",
  timeline: "Go live before the January payroll run.",
  current_solution: "Excel + an old desktop payroll licence.",
  competitors: "Also talking to SageHR.",
};

// How many of the qualification answers are filled, in the order of the master list.
const qualificationOf = (filled) => Object.fromEntries(leadQualification.map((q, i) => [q.key, i < filled ? Q_TEXT[q.key] : ""]));

// [id, code, organisation, prospect_id, customer_id, industry, segment, location, source, products, contact, title, owner, stage, score, filled, age, lastContact, next, value, opportunity_id, failure]
const LEADS = [
  [1, "LED-0044", "Mwangaza Academy", 5, null, "Education", "SME", 6, "Google Ads", [1], "James Njoroge", "Principal", 5, "converted", 82, 7, 70, 45, null, 300000, 1],
  [2, "LED-0054", "Smart Duka Chain", 15, 10, "Retail", "MID", 1, "Website", [1], "Kevin Langat", "Owner", 1, "converted", 76, 7, 52, 3, null, 756000, 4],
  [3, "LED-0066", "Gatsby Insurance", 27, 13, "Insurance", "MID", 1, "Google Ads", [1], "Martin Gitau", "HR", 2, "qualified", 71, 7, 30, 2, 1, 1080000, null],
  [4, "LED-0072", "Rift Valley Millers", null, null, "Manufacturing", "ENT", 5, "Agent / Partner", [1], "Daniel Rotich", "Finance Director", 5, "converted", 88, 7, 110, 30, null, 1980000, 2],
  [5, "LED-0073", "Kampala Retail Hub", null, null, "Retail", "MID", 7, "LinkedIn", [1], "Moses Ssali", "Operations", 4, "converted", 79, 7, 95, 20, null, 768000, 3],
  [6, "LED-0075", "Zawadi Supermarkets", null, null, "Retail", "MID", 4, "Event", [1], "Wanjiru Kamau", "Operations Director", 3, "converted", 74, 7, 64, 4, null, 1092000, 5],
  [7, "LED-0076", "Ufanisi Sacco", null, null, "SACCO", "ENT", 2, "Referral", [2], "Joseph Mutai", "CEO", 2, "converted", 90, 7, 88, 1, null, 1296000, 6],
  [8, "LED-0078", "Neema Hospital", null, null, "Healthcare", "MID", 5, "Website", [1], "Dr. Agnes Wekesa", "Hospital Administrator", 1, "converted", 63, 7, 40, 6, null, 720000, 7],
  [9, "LED-0079", "Mlimani Tea Estates", null, null, "Agriculture", "ENT", 5, "Outbound call", [1], "Samuel Koech", "HR Director", 4, "converted", 58, 7, 36, 12, null, 1440000, 8],
  [10, "LED-0081", "Taifa Engineering", null, null, "Manufacturing", "MID", 6, "LinkedIn", [1], "Grace Akoth", "Finance Manager", 3, "converted", 61, 6, 15, 3, null, 936000, 9],
  [11, "LED-0082", "Uzima Diagnostics", null, null, "Healthcare", "MID", 2, "Referral", [1], "Dr. Ken Ndegwa", "Medical Director", 5, "converted", 66, 7, 70, 15, null, 540000, 10],
  [12, "LED-0083", "Simba Logistics", null, null, "Logistics", "ENT", 3, "Existing customer", [1], "Abdul Rashid", "CFO", 2, "converted", 85, 7, 92, 2, null, 1980000, 11],
  [13, "LED-0084", "Amani Schools Trust", null, null, "Education", "MID", 1, "Event", [1], "Rose Nyambura", "Bursar", 5, "converted", 55, 6, 35, 11, null, 900000, 12],
  [14, "LED-0085", "Kisumu Lakeside Hotel", null, null, "Hospitality", "MID", 4, "Google Ads", [1], "Brenda Anyango", "GM", 4, "converted", 60, 7, 120, 40, null, 720000, 13],
  [15, "LED-0086", "Bahari Fisheries", null, null, "Agriculture", "SME", 3, "Walk-in", [1], "Omar Bakari", "Director", 4, "converted", 42, 5, 80, 35, null, 216000, 14],
  [16, "LED-0087", "Baraka Hardware Ltd", null, null, "Retail", "SME", 6, "Outbound call", [1], "Peter Gachanja", "Owner", 3, "converted", 50, 7, 100, 25, null, 606000, 15],
  [17, "LED-0088", "Nakuru Dairy Co-op", null, null, "SACCO", "ENT", 5, "Event", [2], "John Kirui", "Chairman", 5, "converted", 47, 6, 90, 50, null, 1080000, 16],
  [18, "LED-0089", "Coast General Clinic", null, null, "Healthcare", "MID", 3, "Referral", [1], "Dr. Salma Hassan", "Administrator", 1, "converted", 72, 7, 130, 21, null, 1200000, 17],
  [19, "LED-0091", "Haraka Credit Ltd", null, null, "Banking & Finance", "MID", 1, "Website", [2], "Paul Mwenda", "Head of Credit", 3, "new", 34, 1, 2, null, 0, 650000, null],
  [20, "LED-0092", "Mara Safari Lodges", null, null, "Hospitality", "ENT", 5, "Referral", [1], "Naserian Sankale", "Group Finance", 4, "contacted", 78, 4, 9, 1, 2, 1700000, null],
  [21, "LED-0093", "Kerio Valley Water Co.", null, null, "Government", "ENT", 5, "Event", [1], "Eng. Moses Biwott", "HR & Admin", 2, "qualified", 64, 2, 21, 4, -3, 1300000, null],
  [22, "LED-0094", "Jitegemee Sacco", null, null, "SACCO", "MID", 2, "Agent / Partner", [2], "Lydia Kerubo", "CEO", 2, "contacted", 52, 3, 12, 6, 1, 1080000, null],
  [23, "LED-0095", "Nyeri Coffee Millers", null, null, "Agriculture", "MID", 6, "LinkedIn", [1], "Charles Maina", "Finance", null, "new", 28, 0, 1, null, null, 720000, null],
  [24, "LED-0096", "Lamu Boat Builders", null, null, "Manufacturing", "SME", 3, "Walk-in", [1], "Swaleh Athman", "Owner", 4, "lost", 18, 1, 40, 22, null, 216000, null, { at: "contacted", code: "CON-01", note: "Five calls and two emails over three weeks, no response." }],
  [25, "LED-0097", "Sunrise Bakeries", null, null, "Manufacturing", "SME", 4, "Google Ads", [1], "Ruth Atieno", "Owner", 3, "lost", 30, 4, 33, 14, null, 300000, null, { at: "qualified", code: "FIT-01", note: "12 staff only; offered the Basic package but they will stay on manual.", revisit_on: 180 }],
  [26, "LED-0098", "Kitengela Glass", null, null, "Manufacturing", "MID", 6, "Website", [1], "Edwin Ngugi", "MD", 5, "qualified", 69, 7, 18, 1, 0, 1150000, null],
];

const LEAD_PATH = ["new", "contacted", "qualified", "converted"];

const leadHistory = (stage, failed, age) => {
  const reached = failed ? LEAD_PATH.indexOf(failed.at) : LEAD_PATH.indexOf(stage);
  const span = Math.max(1, Math.floor(age / (reached + 2)));
  const steps = [];
  for (let i = 1; i <= reached; i++) steps.push({ from: LEAD_PATH[i - 1], to: LEAD_PATH[i], at: daysFromNow(-age + span * i), days_in_previous: span });
  if (failed) steps.push({ from: failed.at, to: "lost", at: daysFromNow(-Math.max(1, Math.floor(span / 2))), days_in_previous: span, reason: failed.code });
  return steps.reverse();
};

export const leads = LEADS.map(
  ([id, code, organisation, prospect_id, customer_id, industry, segment, location_id, source, product_ids, contact_name, contact_title, owner_id, stage, score, filled, age, lastContact, next, estimated_value, opportunity_id, failure]) => {
    const failed = failure
      ? { ...failure, revisit_on: failure.revisit_on ? daysFromNow(failure.revisit_on) : null, on: daysFromNow(-Math.max(1, Math.floor(lastContact / 2))), by: staffName(owner_id) }
      : null;
    return {
      id,
      code,
      organisation,
      prospect_id,
      customer_id, // set when the lead is an existing customer buying more
      industry,
      segment,
      location_id,
      source,
      product_ids,
      contact_name,
      contact_title,
      phone: `+254 72${id % 10} ${String(400 + id * 11).slice(-3)} ${String(200 + id * 17).slice(-3)}`,
      email: `${contact_name.split(" ").slice(-1)[0].toLowerCase()}@${organisation.split(" ")[0].toLowerCase()}.co.ke`,
      owner_id,
      stage,
      score,
      qualification: qualificationOf(filled),
      created_at: daysFromNow(-age),
      last_contact_at: lastContact === null ? null : daysFromNow(-lastContact),
      next_action: next === null ? null : { type: ["Call", "Meeting", "Email", "Visit"][id % 4], date: daysFromNow(next), note: stage === "qualified" ? "Agree scope for the opportunity" : "Qualification call" },
      estimated_value,
      opportunity_id,
      failed,
      history: leadHistory(stage, failure, age),
    };
  },
);

export const leadById = (id) => leads.find((l) => l.id === Number(id));
export const leadByCode = (code) => leads.find((l) => l.code === code);

/* ---------------------------------------------------------- Opportunities */

const PIPE = pipelineStages.filter((s) => s.kind === "open").sort((a, b) => a.order - b.order).map((s) => s.key);

// Days spent in each stage before the current one, so the history and velocity read sensibly.
const oppHistory = (o) => {
  const end = o.lost ? o.lost.at : o.stage === "won" ? "contract" : o.stage;
  const reached = PIPE.indexOf(end);
  const before = o.age - o.days_in_stage;
  const span = Math.max(1, Math.floor(before / Math.max(1, reached)));
  const steps = [];
  for (let i = 1; i <= reached; i++) steps.push({ from: PIPE[i - 1], to: PIPE[i], at: daysFromNow(-o.age + span * i), days_in_previous: span, by: staffName(o.owner_id) });
  if (o.stage === "won") steps.push({ from: "contract", to: "won", at: o.won_on, days_in_previous: o.age - span * reached - o.days_in_stage || 5, by: staffName(o.owner_id) });
  if (o.lost) steps.push({ from: o.lost.at, to: "lost", at: o.lost.on, days_in_previous: o.days_in_stage, reason: o.lost.code, by: staffName(o.owner_id) });
  return steps.reverse();
};

const line = (package_id, qty, months = 12) => {
  const p = packages.find((x) => x.id === package_id);
  return { product_id: p.product_id, package_id, qty, months, unit_price: p.price };
};

// [id, lead, customer, organisation, owner, stage, value, close(days), age, daysInStage, next, lines, competitors, extra]
const OPPS = [
  [1, 1, 7, "Mwangaza Academy", 5, "won", 300000, -42, 70, 0, null, [line(1, 1)], [{ id: 4, price: 0, strengths: "Free, familiar", weaknesses: "Errors in PAYE; no leave tracking", status: "Beaten" }], { won_on: daysFromNow(-42) }],
  [2, 4, 8, "Rift Valley Millers", 5, "won", 1980000, -28, 110, 0, null, [line(3, 1)], [{ id: 1, price: 2300000, strengths: "Brand known to the board", weaknesses: "No local payroll support; priced in USD", status: "Beaten" }], { won_on: daysFromNow(-28) }],
  [3, 5, 11, "Kampala Retail Hub", 4, "won", 768000, -18, 95, 0, null, [line(2, 1)], [{ id: 5, price: 690000, strengths: "Cheaper licence", weaknesses: "Partner in Nairobi only, long implementation", status: "Beaten" }], { won_on: daysFromNow(-18) }],
  [4, 2, 10, "Smart Duka Chain", 1, "negotiation", 756000, 12, 45, 9, { type: "Meeting", date: 2, note: "Agree final discount with the owner" }, [line(2, 1)], [{ id: 2, price: 480000, strengths: "Cheaper per employee", weaknesses: "Housing Levy and SHIF updates lag; no shift payroll", status: "Active" }], {}],
  [5, 6, null, "Zawadi Supermarkets", 3, "proposal", 1092000, 25, 60, 12, { type: "Call", date: 1, note: "Walk the ops director through the proposal" }, [line(2, 1)], [{ id: 5, price: 950000, strengths: "All-in-one ERP", weaknesses: "Heavy customisation; no shift or casual payroll out of the box", status: "Active" }, { id: 4, price: 0, strengths: "No cost", weaknesses: "PAYE errors and late P10 penalties", status: "Active" }], {}],
  [6, 7, null, "Ufanisi Sacco", 2, "contract", 1296000, 6, 85, 5, { type: "Follow-up", date: 0, note: "Board resolution and signed contract" }, [line(5, 1), line(6, 2)], [{ id: 3, price: 1400000, strengths: "Used by two neighbouring Saccos", weaknesses: "No M-Pesa STK repayments; on-premise servers", status: "Active" }], {}],
  [7, 8, null, "Neema Hospital", 1, "demo", 720000, 40, 38, 6, { type: "Demo", date: 3, note: "Payroll + rota demo for HR and finance" }, [line(2, 1)], [{ id: 1, price: 820000, strengths: "Strong reporting", weaknesses: "No shift/rota module", status: "Active" }], {}],
  [8, 9, null, "Mlimani Tea Estates", 4, "discovery", 1440000, 55, 34, 19, null, [line(3, 1)], [{ id: 2, price: 1100000, strengths: "Low price per user", weaknesses: "Casual-labour payroll needs workarounds", status: "Active" }], {}],
  [9, 10, null, "Taifa Engineering", 3, "qualified", 936000, 70, 12, 4, { type: "Visit", date: 5, note: "Discovery workshop on site" }, [line(2, 1)], [], {}],
  [10, 11, null, "Uzima Diagnostics", 5, "proposal", 540000, -4, 68, 27, { type: "Email", date: -3, note: "Chase feedback on proposal" }, [line(1, 1)], [{ id: 2, price: 420000, strengths: "Cheaper", weaknesses: "No multi-branch payroll", status: "Active" }], {}],
  [11, 12, null, "Simba Logistics", 2, "negotiation", 1980000, 18, 90, 15, { type: "Meeting", date: 4, note: "Commercial meeting with CFO" }, [line(3, 1)], [{ id: 1, price: 2400000, strengths: "Global brand", weaknesses: "Expensive; no fleet allowances", status: "Active" }, { id: 5, price: 1700000, strengths: "Covers fleet too", weaknesses: "12-month implementation", status: "Active" }], {}],
  [12, 13, null, "Amani Schools Trust", 5, "demo", 900000, 33, 33, 17, null, [line(1, 3)], [], {}],
  [13, 14, null, "Kisumu Lakeside Hotel", 4, "lost", 720000, -30, 120, 9, null, [line(2, 1)], [{ id: 2, price: 540000, strengths: "25% cheaper; monthly billing", weaknesses: "Weaker statutory updates", status: "Won" }], { lost: { at: "proposal", code: "CMP-01", competitor: "Zoho", note: "Went with Zoho People on price; we would not go below 15% discount.", on: daysFromNow(-30) } }],
  [14, 15, null, "Bahari Fisheries", 4, "lost", 216000, -45, 80, 6, null, [line(1, 1)], [], { lost: { at: "demo", code: "PRD-01", competitor: "", note: "Needs piece-rate payroll for casual fish handlers, which we do not support yet.", on: daysFromNow(-45) } }],
  [15, 16, null, "Baraka Hardware Ltd", 3, "lost", 606000, -20, 100, 11, null, [line(1, 1)], [{ id: 2, price: 380000, strengths: "Cheaper", weaknesses: "Weak statutory updates", status: "Active" }], { lost: { at: "negotiation", code: "PRC-02", competitor: "", note: "Owner capped spend at KES 400K a year.", on: daysFromNow(-20) } }],
  [16, 17, null, "Nakuru Dairy Co-op", 5, "lost", 1080000, -55, 90, 20, null, [line(4, 1)], [], { lost: { at: "discovery", code: "TIM-02", competitor: "Musoni", note: "Locked into their current loan system contract until Dec 2027.", on: daysFromNow(-55), revisit_on: daysFromNow(380) } }],
  [17, 18, null, "Coast General Clinic", 1, "lost", 1200000, -12, 130, 8, null, [line(2, 1)], [{ id: 5, price: 1050000, strengths: "HR, finance and pharmacy in one", weaknesses: "Long implementation", status: "Won" }], { lost: { at: "contract", code: "CMP-02", competitor: "Odoo", note: "Board preferred one ERP for pharmacy stock and HR.", on: daysFromNow(-12) } }],
  [18, null, 9, "Harambee Sacco", 2, "discovery", 720000, 48, 14, 8, { type: "Meeting", date: 6, note: "HR scoping with the Sacco's HR officer" }, [line(2, 1)], [], { kind: "Cross-sell" }],
  [19, null, 18, "Pwani Logistics", 4, "qualified", 480000, 35, 5, 5, { type: "Call", date: 2, note: "Confirm number of extra employees" }, [line(3, 1)], [], { kind: "Upsell" }],
];

export const opportunities = OPPS.map(([id, lead_id, customer_id, organisation, owner_id, stage, value, close, age, days_in_stage, next, lines, competitors, extra]) => {
  const lead = leadById(lead_id);
  const st = pipelineStages.find((s) => s.key === stage);
  const products = [...new Set(lines.map((l) => l.product_id))];
  const o = {
    id,
    code: `OPP-${String(id + 100).padStart(4, "0")}`,
    name: `${organisation} – ${products.length > 1 ? "HRM + LMS" : products[0] === 2 ? "Demulla LMS tenant" : "DeeHr360 subscription"}`,
    kind: extra.kind ?? (customer_id && stage !== "won" ? "Upsell" : "New business"),
    lead_id,
    customer_id, // won deals link the customer they created; upsells the customer they grow
    organisation,
    contact_name: lead?.contact_name ?? "Account contact",
    contact_title: lead?.contact_title ?? "",
    source: lead?.source ?? "Existing customer",
    owner_id,
    stage,
    lines,
    product_ids: products,
    value,
    probability: st.probability,
    expected_close: daysFromNow(close),
    created_at: daysFromNow(-age),
    stage_entered_at: daysFromNow(-days_in_stage),
    age,
    days_in_stage,
    next_action: next ? { ...next, date: daysFromNow(next.date) } : null,
    competitors: competitors.map((c) => ({ competitor_id: c.id, ...c })),
    lost: extra.lost ? { ...extra.lost, by: staffName(owner_id) } : null,
    won_on: extra.won_on ?? null,
    // Advanced: model-scored win probability and the next best action it suggests.
    ai_probability: { won: 100, lost: 0 }[stage] ?? Math.min(95, Math.max(5, st.probability + ((id * 7) % 23) - 9)),
    qualification: lead ? lead.qualification : qualificationOf(5),
  };
  o.history = oppHistory(o);
  return o;
});

export const opportunityById = (id) => opportunities.find((o) => o.id === Number(id));
export const openOpportunities = () => opportunities.filter((o) => pipelineStages.find((s) => s.key === o.stage)?.kind === "open");
export const weighted = (o) => Math.round((o.value * o.probability) / 100);

// Why the model scores the deal as it does (Advanced).
export const winFactors = (o) => [
  { label: "Decision maker engaged", effect: o.qualification.decision_maker ? 12 : -15 },
  { label: "Budget confirmed", effect: o.qualification.budget ? 10 : -12 },
  { label: `${o.competitors.filter((c) => c.status === "Active").length} active competitor(s)`, effect: -6 * o.competitors.filter((c) => c.status === "Active").length },
  { label: o.days_in_stage > (pipelineStages.find((s) => s.key === o.stage)?.max_days ?? 99) ? "Stalled beyond stage limit" : "Moving within stage limit", effect: o.days_in_stage > (pipelineStages.find((s) => s.key === o.stage)?.max_days ?? 99) ? -10 : 5 },
  { label: o.source === "Referral" || o.source === "Existing customer" ? `Source: ${o.source}` : "Source has average win rate", effect: o.source === "Referral" || o.source === "Existing customer" ? 8 : 0 },
];

export const nextBestAction = (o) => {
  if (!o.next_action) return { icon: "iconoir-calendar", text: "No next action is set. Book a follow-up — an open opportunity must always have one." };
  if (o.days_in_stage > (pipelineStages.find((s) => s.key === o.stage)?.max_days ?? 99)) return { icon: "iconoir-clock", text: "Stalled past the stage limit. Ask the sponsor for a decision date or escalate to the Sales Manager." };
  if (o.competitors.some((c) => c.status === "Active")) return { icon: "iconoir-shield-check", text: "Send the competitor comparison sheet and a reference call with a similar customer." };
  if (o.stage === "demo") return { icon: "iconoir-presentation", text: "Book the demo with the decision maker present, not only the evaluators." };
  return { icon: "iconoir-send", text: "Move to proposal: send a quote while interest is high." };
};

export const activityFor = (o) => [
  { at: `${daysFromNow(-2)}T11:20:00`, type: "Call", title: `Call with ${o.contact_name}`, body: "Walked through pricing options and implementation timeline.", by: staffName(o.owner_id) },
  { at: `${daysFromNow(-6)}T15:00:00`, type: "Email", title: "Sent case study", body: "Shared a case study from a similar customer in the same industry.", by: staffName(o.owner_id) },
  { at: `${daysFromNow(-9)}T10:00:00`, type: "Status change", title: "Stage changed", body: "Moved forward after the sponsor confirmed budget.", by: staffName(o.owner_id) },
  { at: `${daysFromNow(-14)}T09:30:00`, type: "Meeting", title: "Discovery meeting", body: "Met HR, finance and IT; mapped current process and pain points.", by: "Brian Otieno" },
];

/* ---------------------------------------------------- Proposals & quotes */

export const quoteStatuses = [
  { key: "draft", name: "Draft", tone: "secondary" },
  { key: "submitted", name: "Submitted", tone: "warning" },
  { key: "approved", name: "Approved", tone: "info" },
  { key: "rejected", name: "Rejected", tone: "danger" },
  { key: "sent", name: "Sent", tone: "primary" },
  { key: "accepted", name: "Accepted", tone: "success" },
  { key: "expired", name: "Expired", tone: "dark" },
];
export const quoteStatus = (key) => quoteStatuses.find((s) => s.key === key);

const ql = (package_id, qty, months, discount_pct = 0) => ({ ...line(package_id, qty, months), discount_pct, description: "" });

const chain = (roles, states) =>
  roles.map((role, i) => ({
    level: i + 1,
    role,
    approver: { "Sales Manager": "Jane Wanjiku", "Head of Sales": "Brian Otieno", Finance: "Mercy Wambui" }[role],
    status: states[i] ?? "waiting",
    at: ["approved", "rejected"].includes(states[i]) ? `${daysFromNow(-(i + 2))}T10:${10 + i}:00` : null,
    note: states[i] === "rejected" ? "Margin too thin at 25%; offer 3 months free implementation instead." : states[i] === "approved" ? "OK given multi-year term." : "",
  }));

// [id, opportunity, kind, version, status, lines, created, valid, approval states, versions]
const QUOTES = [
  [1, 1, "Quotation", 1, "accepted", [ql(1, 1, 12)], -55, -25, [], []],
  [2, 2, "Proposal", 3, "accepted", [ql(3, 1, 12, 12)], -40, -10, ["approved"], [[1, -70, "Initial proposal"], [2, -55, "Moved to Advanced (multi-company)"]]],
  [3, 3, "Quotation", 2, "accepted", [ql(2, 1, 12, 5)], -30, 0, [], [[1, -45, "Basic package"]]],
  [4, 4, "Quotation", 2, "submitted", [ql(2, 1, 12, 15)], -3, 27, ["pending"], [[1, -15, "List price"]]],
  [5, 5, "Proposal", 1, "sent", [ql(2, 1, 12, 8)], -12, 18, [], []],
  [6, 6, "Quotation", 2, "approved", [ql(5, 1, 12, 18), ql(6, 2, 12, 18)], -6, 24, ["approved"], [[1, -20, "Standard pricing"]]],
  [7, 10, "Proposal", 1, "sent", [ql(1, 1, 12, 0)], -27, -1, [], []],
  [8, 11, "Quotation", 2, "draft", [ql(3, 1, 12, 12)], -1, 29, [], [[1, -10, "25% discount – rejected by Head of Sales"]]],
  [9, 13, "Quotation", 1, "expired", [ql(2, 1, 12, 15)], -60, -30, ["approved"], []],
  [10, 15, "Quotation", 1, "rejected", [ql(1, 1, 12, 25)], -26, 4, ["approved", "rejected"], []],
  [11, 7, "Proposal", 1, "draft", [ql(2, 1, 12, 0)], 0, 30, [], []],
];

export const quoteTotals = (q) => {
  const gross = q.lines.reduce((s, l) => s + l.qty * l.unit_price * l.months, 0);
  const discount = q.lines.reduce((s, l) => s + (l.qty * l.unit_price * l.months * (l.discount_pct || 0)) / 100, 0);
  const net = gross - discount;
  const vat = (net * discountPolicy.vat) / 100;
  return { gross, discount, net, vat, total: net + vat, discount_pct: gross ? (discount / gross) * 100 : 0 };
};

export const quotations = QUOTES.map(([id, opportunity_id, kind, version, status, lines, created, valid, approvals, versions]) => {
  const o = opportunityById(opportunity_id);
  const q = {
    id,
    number: `${kind === "Proposal" ? "PRP" : "QUO"}-2026-${String(id + 200).padStart(4, "0")}`,
    kind,
    version,
    status,
    opportunity_id,
    customer_id: o.customer_id,
    organisation: o.organisation,
    contact_name: o.contact_name,
    owner_id: o.owner_id,
    lines,
    created_at: daysFromNow(created),
    valid_until: daysFromNow(valid),
    sent_at: ["sent", "accepted", "expired"].includes(status) ? daysFromNow(created + 1) : null,
    accepted_at: status === "accepted" ? daysFromNow(created + 8) : null,
    terms: "Annual subscription billed quarterly in advance. Implementation billed separately. Prices exclusive of VAT.",
  };
  const t = quoteTotals(q);
  q.approval = { required: t.discount_pct > discountPolicy.threshold, chain: chain(approvalChainFor(t.discount_pct), approvals) };
  q.versions = [
    ...versions.map(([v, at, note]) => ({ version: v, at: daysFromNow(at), by: staffName(o.owner_id), note, total: Math.round(t.total * (1 + (version - v) * 0.06)) })),
    { version, at: q.created_at, by: staffName(o.owner_id), note: "Current version", total: t.total },
  ].reverse();
  return q;
});

export const quotationById = (id) => quotations.find((q) => q.id === Number(id));
export const quotesFor = (opportunityId) => quotations.filter((q) => q.opportunity_id === Number(opportunityId));
export const approvalState = (q) => {
  if (!q.approval.required) return { label: "Not needed", tone: "light" };
  if (q.approval.chain.some((c) => c.status === "rejected")) return { label: "Rejected", tone: "danger" };
  if (q.approval.chain.every((c) => c.status === "approved")) return { label: "Approved", tone: "success" };
  if (q.approval.chain.some((c) => c.status === "pending")) return { label: `Pending · ${q.approval.chain.find((c) => c.status === "pending").role}`, tone: "warning" };
  return { label: "Not submitted", tone: "secondary" };
};

/* ------------------------------------------------------------------ Demos */

// [id, opportunity, products, days from now, hour, mode, presenter, attendees, status, outcome, feedback, follow-up]
const DEMOS = [
  [1, 7, [1], 3, 10, "On-site", 1, [["Dr. Agnes Wekesa", "Hospital Administrator"], ["Mary Chepkemoi", "HR Manager"], ["Felix Otieno", "Finance Manager"]], "scheduled", null, "", null],
  [2, 12, [1], 6, 14, "Online", 5, [["Rose Nyambura", "Bursar"], ["Head teachers (3)", "Users"]], "scheduled", null, "", null],
  [3, 5, [1], -14, 11, "On-site", 3, [["Wanjiru Kamau", "Operations Director"], ["Store managers (4)", "Users"]], "completed", "positive", "Liked shift rota and casual-worker payroll. Asked about biometric clock-in.", { type: "Email", date: -12, note: "Sent biometric integration note and proposal" }],
  [4, 11, [1], -25, 9, "On-site", 2, [["Abdul Rashid", "CFO"], ["Janet Wambua", "HR Manager"], ["IT Lead", "IT"]], "completed", "positive", "CFO wants fleet allowances handled; confirmed in Advanced package.", { type: "Meeting", date: 4, note: "Commercial meeting" }],
  [5, 10, [1], -35, 15, "Online", 5, [["Dr. Ken Ndegwa", "Medical Director"], ["Accountant", "Finance"]], "completed", "neutral", "Liked reports; worried about migrating 3 years of history.", { type: "Call", date: -30, note: "Migration options call" }],
  [6, 14, [1], -52, 10, "On-site", 4, [["Omar Bakari", "Director"]], "completed", "negative", "Piece-rate payroll for casual handlers is a must; we do not support it yet.", null],
  [7, 8, [1], -8, 11, "Online", 4, [["Samuel Koech", "HR Director"]], "no_show", null, "Client did not join; rescheduling.", { type: "Call", date: -6, note: "Rebook demo" }],
  [8, 6, [2], -40, 10, "On-site", 2, [["Joseph Mutai", "CEO"], ["Credit Manager", "Users"], ["Board member", "Observer"]], "completed", "positive", "Board impressed by M-Pesa STK repayments, penalty rules and SASRA reports.", { type: "Email", date: -38, note: "Proposal sent" }],
  [9, 2, [1], -80, 9, "On-site", 5, [["Daniel Rotich", "Finance Director"], ["HR team (3)", "Users"]], "completed", "positive", "Shift payroll for the mill fully covered.", { type: "Email", date: -78, note: "Proposal v1" }],
  [10, 9, [1], 12, 10, "On-site", 3, [["Grace Akoth", "Finance Manager"]], "scheduled", null, "", null],
  [11, 17, [1], -70, 14, "Online", 1, [["Dr. Salma Hassan", "Administrator"]], "cancelled", null, "Client cancelled; re-done on site the week after.", null],
];

export const demoStatuses = [
  { key: "scheduled", name: "Scheduled", tone: "primary" },
  { key: "completed", name: "Completed", tone: "success" },
  { key: "no_show", name: "No-show", tone: "warning" },
  { key: "cancelled", name: "Cancelled", tone: "secondary" },
];
export const demoOutcomes = [
  { key: "positive", name: "Positive", tone: "success" },
  { key: "neutral", name: "Neutral", tone: "warning" },
  { key: "negative", name: "Negative", tone: "danger" },
];

export const demos = DEMOS.map(([id, opportunity_id, product_ids, day, hour, mode, presenter_id, attendees, status, outcome, feedback, follow]) => {
  const o = opportunityById(opportunity_id);
  return {
    id,
    code: `DEM-${String(id + 300).padStart(4, "0")}`,
    opportunity_id,
    organisation: o.organisation,
    product_ids,
    scheduled_at: `${daysFromNow(day)}T${String(hour).padStart(2, "0")}:00:00`,
    duration_min: 90,
    mode,
    location: mode === "Online" ? "Google Meet" : `${o.organisation} offices`,
    presenter_id,
    attendees: attendees.map(([name, title]) => ({ name, title })),
    internal: [staffName(o.owner_id), staffName(presenter_id), "Kevin Mwangi"].filter((v, i, a) => a.indexOf(v) === i),
    status,
    outcome,
    feedback,
    follow_up: follow ? { ...follow, date: daysFromNow(follow.date) } : null,
  };
});

export const demosFor = (opportunityId) => demos.filter((d) => d.opportunity_id === Number(opportunityId));

/* --------------------------------------------------------- Sales targets */

// Quarterly new-business targets per salesperson (Settings → Sales Targets). NEW MASTER.
export const salesTargets = [
  { owner_id: 1, period: "2026-Q4", target: 3500000 },
  { owner_id: 2, period: "2026-Q4", target: 5000000 },
  { owner_id: 3, period: "2026-Q4", target: 2500000 },
  { owner_id: 4, period: "2026-Q4", target: 3000000 },
  { owner_id: 5, period: "2026-Q4", target: 3000000 },
];
// Monthly company target, for the forecast chart.
export const monthlyTargets = { "2026-10": 4500000, "2026-11": 5000000, "2026-12": 5500000, "2027-01": 4000000, "2027-02": 4000000 };
